import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Table } from "@opendate-app/setlist";
import ActionsMenu from "../../shared/components/ActionsMenu";
import KillModal from "../../ConfirmTicketing/components/KillModal";
import KillsHeader from "../../ConfirmTicketing/components/KillsHeader";
import { HoldsKillsChartView } from "../../ConfirmTicketing/components/HoldsKillsChartView";
import { chartShape, contextFromEvent, headerEvents } from "./chartHeaders";
import { useChartPlanning } from "./usePlanningEvent";
import { blankTicketType, chartContextOf, takenSeats } from "../scheduleBuilder";

const seatCount = (kill) =>
  (kill.objects || []).length + Object.values(kill.areaPlaces || {}).reduce((sum, count) => sum + (Number(count) || 0), 0);

const KillsTab = ({ charts, kills, holds = [], onChange }) => {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const { chart, select, planning } = useChartPlanning(charts);
  const chartOpen = Boolean(chart.open);

  useEffect(() => {
    if (chartOpen) planning.load();
  }, [chartOpen, chart.context]);
  const shown = kills.filter((kill) => chartContextOf(kill) === chart.context);
  const editing = shown.find((kill) => kill.id === editingId) || null;
  const initial = useMemo(
    () => editing && { name: editing.name, objects: editing.objects, area_places: editing.areaPlaces },
    [editing],
  );

  if (charts.length === 0) {
    return (
      <div className="series-tab-panel">
        <div className="text-center my-5" data-testid="kills-need-seating">Set up a seating chart to manage Kills.</div>
      </div>
    );
  }

  const close = () => {
    setAdding(false);
    setEditingId(null);
  };
  const begin = (id) => {
    planning.load();
    id ? setEditingId(id) : setAdding(true);
  };
  const switchChart = (context) => {
    close();
    select(context);
  };

  return (
    <div className="series-tab-panel">
      <KillsHeader
        events={headerEvents(charts, true)}
        selectedEvent={{ id: chart.context || "admission" }}
        onSelectEvent={(value) => switchChart(contextFromEvent(value))}
        onClickAdd={() => begin(null)}
        addDisabled={!chart.open}
        subtitle="Take seats off sale on every show of this type, like a camera position or sound desk."
      />
      {!chart.open && (
        <p className="series-edit-hint" data-testid="kills-need-chart">Choose a seating chart on Admission to kill seats.</p>
      )}
      {planning.error && <p className="series-schedule-error">{planning.error}</p>}

      <Table
        columns={[
          { key: "name", header: "Name", primary: true, render: (kill) => kill.name },
          { key: "seats", header: "Seats", render: seatCount },
          {
            key: "actions",
            header: "",
            align: "right",
            width: "56px",
            render: (kill) => (
              <ActionsMenu
                actions={[
                  { label: "Edit", icon: "fal fa-pencil", onClick: () => begin(kill.id) },
                  {
                    label: "Delete",
                    icon: "fal fa-trash-alt",
                    danger: true,
                    onClick: () => onChange(kills.filter((candidate) => candidate.id !== kill.id)),
                  },
                ]}
              />
            ),
          },
        ]}
        data={shown}
        getRowKey={(kill) => kill.id}
        empty="No kills yet."
      />
      {chartOpen && planning.event && (
        <HoldsKillsChartView
          selectedEvent={{ seatsio_event_id: planning.event.eventKey }}
          holds={holds.filter((hold) => chartContextOf(hold) === chart.context)}
          kills={shown}
          seatsIOPublicKey={planning.event.publicKey || ""}
        />
      )}

      {planning.event && (
        <KillModal
          key={chart.context || "admission"}
          open={adding || Boolean(editing)}
          onClose={close}
          title={editing ? "Edit Kill" : "Add Kill"}
          seatsIOPublicKey={planning.event.publicKey || ""}
          selectedEvent={{ seatsio_event_id: planning.event.eventKey }}
          initialKill={initial}
          takenObjects={takenSeats({ holds, kills, exceptId: editing && editing.id, chartContext: chart.context })}
          onSubmit={(kill) => {
            const row = { name: kill.name, objects: kill.objects, areaPlaces: kill.area_places };
            onChange(
              editing
                ? kills.map((candidate) => (candidate.id === editing.id ? { ...candidate, ...row } : candidate))
                : [...kills, { ...row, id: `series-kill-${blankTicketType().id}`, ...(chart.context && { chartContext: chart.context }) }],
            );
            close();
            return Promise.resolve();
          }}
        />
      )}
    </div>
  );
};

KillsTab.propTypes = {
  charts: PropTypes.arrayOf(chartShape).isRequired,
  kills: PropTypes.arrayOf(PropTypes.object).isRequired,
  holds: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
};

export default KillsTab;
